// restaurants.js
import React, { useState, useEffect } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import { FiEye, FiUser, FiStar, FiX, FiCheck, FiMapPin, FiCoffee, FiArrowLeft } from 'react-icons/fi';
import { HiOutlineLocationMarker } from 'react-icons/hi';
import { BiStore, BiTime, BiCodeAlt } from 'react-icons/bi';
import { useNavigate } from 'react-router-dom';
import { apiCall } from '../config/api-request';

function Restaurants() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [selectedAction, setSelectedAction] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [modalClosing, setModalClosing] = useState(false);
    const [restaurants, setRestaurants] = useState([]);

    const fetchRestaurants = async () => {
        try {
            setLoading(true);
            const result = await apiCall(
                {
                    api: 'restaurant-list-admin'
                },
                true
            );

            if (result.status === true) {
                setRestaurants(result.data.restaurant || []);
            } else {
                toast.error(result.message || "Failed to load restaurants data");
            }
        } catch (error) {
            console.error("Error fetching restaurants:", error);
            toast.error("Failed to load restaurants data");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRestaurants();
    }, []);

    const handleViewDetails = (item) => {
        setSelectedItem(item);
        setSelectedAction('');
        setShowModal(true);
        setModalClosing(false);
    };

    const handleActionSubmit = async () => {
        if (!selectedAction) {
            toast.error("Please select an action");
            return;
        }

        setActionLoading(true);

        try {
            const result = await apiCall(
                {
                    api: 'process-action-admin',
                    type: 'restaurant',
                    code: selectedItem.code || selectedItem.id,
                    action: selectedAction
                },
                true
            );

            if (result.status === true) {
                toast.success(`Restaurant "${selectedAction}" completed successfully`);
                handleCloseModal();
                await fetchRestaurants();
            } else {
                toast.error(result.message || "Failed to process action");
            }
        } catch (error) {
            console.error("Error processing action:", error);
            toast.error("Failed to process action");
        } finally {
            setActionLoading(false);
        }
    };

    useEffect(() => {
        const handleEscKey = (event) => {
            if (event.key === 'Escape' && showModal && !modalClosing) {
                handleCloseModal();
            }
        };
        document.addEventListener('keydown', handleEscKey);
        return () => document.removeEventListener('keydown', handleEscKey);
    }, [showModal, modalClosing]);

    const handleCloseModal = () => {
        setModalClosing(true);
        setTimeout(() => {
            setShowModal(false);
            setSelectedAction('');
            setSelectedItem(null);
            setActionLoading(false);
            setModalClosing(false);
        }, 200);
    };

    // Skeleton Loader for Table
    const SkeletonRow = () => (
        <tr className="animate-pulse">
            <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-28"></div></td>
            <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-32"></div></td>
            <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-24"></div></td>
            <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-40"></div></td>
            <td className="px-6 py-4"><div className="h-6 bg-gray-200 rounded w-20"></div></td>
            <td className="px-6 py-4"><div className="h-8 bg-gray-200 rounded w-24"></div></td>
        </tr>
    );

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
            {/* Header */}
            <header className="fixed top-0 left-0 right-0 bg-white shadow-lg z-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        {/* Back Button */}
                        <button
                            onClick={() => navigate(-1)}
                            className="bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-200 hover:to-gray-300 p-2.5 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg group"
                        >
                            <FiArrowLeft className="w-5 h-5 text-gray-700 group-hover:text-gray-900" />
                        </button>

                        {/* Centered Title */}
                        <div className="flex items-center absolute left-1/2 transform -translate-x-1/2">
                            <span className="ml-3 text-xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
                                Hotels Management
                            </span>
                        </div>

                        {/* Empty div for spacing */}
                        <div className="w-10"></div>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="pt-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    {/* Restaurants Table */}
                    <div className="bg-white rounded-xl shadow-md overflow-hidden">
                        <div className="px-6 py-4 border-b border-orange-600 bg-gradient-to-r from-orange-700 to-orange-800">
                            <h2 className="text-lg font-semibold text-white">Restaurants List</h2>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full min-w-[800px]">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rating</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {loading ? (
                                        <>
                                            <SkeletonRow />
                                            <SkeletonRow />
                                            <SkeletonRow />
                                        </>
                                    ) : (
                                        <>
                                            {restaurants.map((restaurant) => (
                                                <tr key={restaurant.code} className="hover:bg-gray-50 transition">
                                                    <td className="px-6 py-4 text-sm font-mono text-gray-600">
                                                        <div className="flex items-center space-x-2">
                                                            <span>{restaurant.code}</span>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{restaurant.name}</td>
                                                    <td className="px-6 py-4 text-sm text-gray-600">
                                                        <div className="flex items-center space-x-1">
                                                            <FiStar className="w-4 h-4 text-yellow-400 fill-current" />
                                                            <span>{restaurant.rating}</span>
                                                            {restaurant.review && restaurant.review !== '0' && (
                                                                <span className="ml-1 text-xs text-gray-500">({restaurant.review} reviews)</span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-gray-500">
                                                        <div className="flex items-center space-x-1">
                                                            <HiOutlineLocationMarker className="w-4 h-4 text-gray-400" />
                                                            <span>{restaurant.location || 'N/A'}</span>
                                                        </div>
                                                        {restaurant.longitude && restaurant.latitude && (
                                                            <div className="text-xs text-gray-400 mt-1">
                                                                {parseFloat(restaurant.latitude).toFixed(4)}, {parseFloat(restaurant.longitude).toFixed(4)}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <span className={`px-2 py-1 text-xs rounded-full ${restaurant.admin_status === '0' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                                                            }`}>
                                                            {restaurant.admin_status === '0' ? 'Pending' : 'Approved'}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <button
                                                            onClick={() => handleViewDetails(restaurant)}
                                                            className="px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white text-sm rounded-lg hover:from-blue-600 hover:to-blue-700 transition flex items-center space-x-2 shadow-md"
                                                        >
                                                            <FiEye className="w-4 h-4" />
                                                            <span>View Details</span>
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                            {restaurants.length === 0 && !loading && (
                                                <tr>
                                                    <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                                                        No restaurants found
                                                    </td>
                                                </tr>
                                            )}
                                        </>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </main>

            {/* Approval/Reject Modal */}
            {(showModal || modalClosing) && selectedItem && (
                <div
                    className={`fixed inset-0 z-50 overflow-y-auto transition-all duration-300 ${modalClosing ? 'opacity-0' : 'opacity-100'
                        }`}
                >
                    <div className="flex items-center justify-center min-h-screen px-4 py-8">
                        {/* Backdrop */}
                        <div
                            className={`fixed inset-0 bg-black transition-opacity duration-300 ${modalClosing ? 'opacity-0' : 'opacity-50'
                                }`}
                            onClick={handleCloseModal}
                        ></div>

                        {/* Modal Container */}
                        <div
                            className={`relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col transform transition-all duration-300 ${modalClosing ? 'scale-95 opacity-0' : 'scale-100 opacity-100'
                                }`}
                        >
                            {/* Fixed Header */}
                            <div className="bg-gradient-to-r from-orange-600 to-orange-700 rounded-t-2xl px-6 py-4 flex-shrink-0">
                                <div className="flex justify-between items-center">
                                    <div>
                                        <h3 className="text-xl font-bold text-white">
                                            {selectedItem.name}
                                        </h3>
                                        <p className="text-orange-100 text-sm mt-1">Restaurant Details</p>
                                    </div>
                                    <button
                                        onClick={handleCloseModal}
                                        className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
                                    >
                                        <FiX className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>

                            {/* Scrollable Body */}
                            <div className="flex-1 overflow-y-auto px-6 py-4">
                                <div className="space-y-4">
                                    {/* Restaurant Information */}
                                    <div className="bg-gray-50 rounded-lg p-4">
                                        <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                                            <BiStore className="w-4 h-4 mr-2" />
                                            Restaurant Information
                                        </h4>
                                        <div className="grid grid-cols-2 gap-3 text-sm">
                                            <div className="text-gray-500">Code:</div>
                                            <div className="text-gray-900 font-mono">{selectedItem.code}</div>

                                            <div className="text-gray-500">Name:</div>
                                            <div className="text-gray-900 font-medium">{selectedItem.name}</div>

                                            <div className="text-gray-500">Rating:</div>
                                            <div className="text-gray-900">{selectedItem.rating} ⭐</div>

                                            {selectedItem.review && selectedItem.review !== '0' && (
                                                <>
                                                    <div className="text-gray-500">Reviews:</div>
                                                    <div className="text-gray-900">{selectedItem.review}</div>
                                                </>
                                            )}

                                            <div className="text-gray-500">Status:</div>
                                            <div className="text-gray-900">{selectedItem.status === '1' ? 'Active' : 'Inactive'}</div>

                                            <div className="text-gray-500">Admin Status:</div>
                                            <div className={`font-semibold ${selectedItem.admin_status === '0' ? 'text-yellow-600' : 'text-green-600'
                                                }`}>
                                                {selectedItem.admin_status === '0' ? 'Pending' : 'Approved'}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Location Information */}
                                    {(selectedItem.location || selectedItem.longitude || selectedItem.latitude) && (
                                        <div className="bg-gray-50 rounded-lg p-4">
                                            <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                                                <FiMapPin className="w-4 h-4 mr-2" />
                                                Location Information
                                            </h4>
                                            <div className="grid grid-cols-2 gap-3 text-sm">
                                                {selectedItem.location && (
                                                    <>
                                                        <div className="text-gray-500">Location:</div>
                                                        <div className="text-gray-900">{selectedItem.location}</div>
                                                    </>
                                                )}
                                                {selectedItem.latitude && (
                                                    <>
                                                        <div className="text-gray-500">Latitude:</div>
                                                        <div className="text-gray-900 font-mono text-xs">{selectedItem.latitude}</div>
                                                    </>
                                                )}
                                                {selectedItem.longitude && (
                                                    <>
                                                        <div className="text-gray-500">Longitude:</div>
                                                        <div className="text-gray-900 font-mono text-xs">{selectedItem.longitude}</div>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* User Information */}
                                    <div className="bg-gray-50 rounded-lg p-4">
                                        <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                                            <FiUser className="w-4 h-4 mr-2" />
                                            Owner Information
                                        </h4>
                                        <div className="grid grid-cols-2 gap-3 text-sm">
                                            <div className="text-gray-500">User ID:</div>
                                            <div className="text-gray-900">{selectedItem.user_id}</div>

                                            <div className="text-gray-500">Name:</div>
                                            <div className="text-gray-900 font-medium">{selectedItem.user_name}</div>

                                            <div className="text-gray-500">Email:</div>
                                            <div className="text-gray-900 text-xs break-all">{selectedItem.user_email}</div>

                                            <div className="text-gray-500">Mobile:</div>
                                            <div className="text-gray-900">{selectedItem.user_mobile}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Fixed Footer with Actions */}
                            <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 rounded-b-2xl flex-shrink-0">
                                <div className="flex flex-col sm:flex-row gap-3">
                                    <button
                                        onClick={() => setSelectedAction('approve')}
                                        className={`flex-1 px-4 py-2.5 rounded-lg transition-all duration-200 flex items-center justify-center space-x-2 text-sm font-medium ${selectedAction === 'approve'
                                            ? 'bg-green-600 text-white shadow-md scale-105'
                                            : 'bg-green-100 text-green-700 hover:bg-green-200'
                                            }`}
                                    >
                                        <FiCheck className="w-4 h-4" />
                                        <span>Approve</span>
                                    </button>
                                    <button
                                        onClick={() => setSelectedAction('reject')}
                                        className={`flex-1 px-4 py-2.5 rounded-lg transition-all duration-200 flex items-center justify-center space-x-2 text-sm font-medium ${selectedAction === 'reject'
                                            ? 'bg-red-600 text-white shadow-md scale-105'
                                            : 'bg-red-100 text-red-700 hover:bg-red-200'
                                            }`}
                                    >
                                        <FiX className="w-4 h-4" />
                                        <span>Reject</span>
                                    </button>
                                    <button
                                        onClick={handleActionSubmit}
                                        disabled={!selectedAction || actionLoading}
                                        className={`px-6 py-2.5 rounded-lg transition-all duration-200 flex items-center justify-center space-x-2 text-sm font-medium ${selectedAction && !actionLoading
                                            ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800 shadow-md'
                                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                            }`}
                                    >
                                        {actionLoading ? (
                                            <>
                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                                <span>Processing...</span>
                                            </>
                                        ) : (
                                            <span>Submit</span>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <Toaster
                position="top-right"
                toastOptions={{
                    duration: 4000,
                    style: {
                        background: '#363636',
                        color: '#fff',
                    },
                    success: {
                        duration: 3000,
                        iconTheme: {
                            primary: '#10B981',
                            secondary: '#fff',
                        },
                    },
                    error: {
                        duration: 4000,
                        iconTheme: {
                            primary: '#EF4444',
                            secondary: '#fff',
                        },
                    },
                }}
            />
        </div>
    );
}

export default Restaurants;